import { createContext, startTransition, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { api } from "./api";
import type { BootstrapData } from "./types";

interface AppContextValue {
  data: BootstrapData | null;
  loading: boolean;
  error: string;
  refresh: () => Promise<void>;
}

const AppContext = createContext<AppContextValue | null>(null);

export function AppProvider({ children }: { children: ReactNode }) {
  const [data, setData] = useState<BootstrapData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const mountedRef = useRef(false);
  const requestRef = useRef<Promise<void> | null>(null);
  const controllerRef = useRef<AbortController | null>(null);

  const refresh = useCallback(() => {
    if (requestRef.current) return requestRef.current;
    const controller = new AbortController();
    controllerRef.current = controller;
    let current!: Promise<void>;
    current = api.bootstrap(controller.signal)
      .then((next) => {
        if (!mountedRef.current) return;
        startTransition(() => {
          setData(next);
          setError("");
        });
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted || !mountedRef.current) return;
        setError(cause instanceof Error ? cause.message : "工作站数据加载失败。");
      })
      .finally(() => {
        if (requestRef.current === current) requestRef.current = null;
        if (controllerRef.current === controller) controllerRef.current = null;
        if (mountedRef.current) setLoading(false);
      });
    requestRef.current = current;
    return current;
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    void refresh();
    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    const timer = window.setInterval(refreshWhenVisible, 15000);
    document.addEventListener("visibilitychange", refreshWhenVisible);
    window.addEventListener("focus", refreshWhenVisible);
    return () => {
      mountedRef.current = false;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
      window.removeEventListener("focus", refreshWhenVisible);
      controllerRef.current?.abort();
      controllerRef.current = null;
      requestRef.current = null;
    };
  }, [refresh]);

  const value = useMemo(() => ({ data, loading, error, refresh }), [data, loading, error, refresh]);
  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useAppData() {
  const value = useContext(AppContext);
  if (!value) throw new Error("useAppData must be used inside AppProvider");
  return value;
}
